import { useCallback, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useToast } from '@/ui';
import {
  apiErrorName,
  createChannelDiligence,
  getChannelIntakeDetail,
} from '@/lib/recepthor-api';
import { qk } from '@/lib/queryKeys';
import { diligenceFailure, REFETCH_ON } from './channelDiligenceCopy';
import {
  buildBody,
  hasErrors,
  newRow,
  validateDiligence,
} from './channelDiligenceForm';

// Addresses of an accepted channel pedido: reads the intake detail (the same
// query the charge panel uses, REC-2041) and creates the diligence for its
// address (REC-1400). The form keeps what was typed after an error, so a retry
// sends the same thing.
export function useChannelDiligence({ intakeId, organizationId, enabled }) {
  const toast = useToast();
  const queryClient = useQueryClient();

  const detailKey = qk.channelIntakeQueue.detail(intakeId, organizationId);
  const detailQ = useQuery({
    queryKey: detailKey,
    queryFn: () => getChannelIntakeDetail(intakeId, { organizationId }),
    enabled: enabled && Boolean(intakeId),
  });
  const detail = detailQ.data ?? null;

  const [rows, setRows] = useState(() => [newRow()]);
  const [deadline, setDeadline] = useState('');
  const [notes, setNotes] = useState('');
  const [submitted, setSubmitted] = useState(false);
  // Bumped on a submit the client rejects, so the view can focus the first
  // invalid field.
  const [invalidCount, setInvalidCount] = useState(0);
  // What the POST answered, tagged with when the GET had last answered by
  // then: it stands in until the next GET that answers, and survives one that
  // fails.
  const [result, setResult] = useState(null);
  const detailUpdatedAt = detailQ.dataUpdatedAt;
  const created =
    result && result.at === detailUpdatedAt ? result.diligence : null;
  // Bumped when the create succeeds, so the view can move focus to the result.
  const [createdCount, setCreatedCount] = useState(0);
  // Sync guard: two clicks before the mutation paints its pending state.
  const busyRef = useRef(false);

  const errors = useMemo(
    () => validateDiligence({ deadline, rows }),
    [deadline, rows],
  );

  const mutation = useMutation({
    mutationFn: (body) => createChannelDiligence(body, { organizationId }),
    onSuccess: (data, body) => {
      // One diligence per address and the form sends one: the answer carries no
      // deadline, so the one sent stands in for the summary.
      const diligence = data?.diligences?.[0]
        ? { ...data.diligences[0], deadline: body.addresses[0].deadline }
        : null;
      setResult({ diligence, at: detailUpdatedAt });
      setCreatedCount((n) => n + 1);
      toast({
        kind: 'success',
        message:
          diligence?.created === false
            ? 'La diligencia ya existía, se retomó.'
            : 'Diligencia creada.',
      });
      // The charge panel reads the same detail: it refreshes with this. The
      // GET rules once it answers again (a cancelled diligence comes back as
      // `liveTask: null`).
      queryClient.invalidateQueries({ queryKey: qk.channelIntakeQueue.all() });
    },
    onError: (e) => {
      toast({ kind: 'err', ...diligenceFailure(e, apiErrorName(e?.body)) });
      if (REFETCH_ON.includes(apiErrorName(e?.body))) {
        queryClient.invalidateQueries({
          queryKey: qk.channelIntakeQueue.all(),
        });
      }
    },
  });

  const submit = useCallback(async () => {
    if (busyRef.current) return;
    setSubmitted(true);
    if (hasErrors(errors)) {
      setInvalidCount((n) => n + 1);
      return;
    }
    busyRef.current = true;
    try {
      await mutation.mutateAsync(
        buildBody({ intakeId, deadline, notes, rows }),
      );
    } catch {
      // onError already told the user.
    } finally {
      busyRef.current = false;
    }
  }, [deadline, errors, intakeId, mutation, notes, rows]);

  const updateRow = useCallback(
    (key, patch) =>
      setRows((r) => r.map((x) => (x.key === key ? { ...x, ...patch } : x))),
    [],
  );

  const refresh = useCallback(
    () =>
      queryClient.invalidateQueries({ queryKey: qk.channelIntakeQueue.all() }),
    [queryClient],
  );

  return {
    refreshing: detailQ.isFetching,
    onRefresh: refresh,
    loading: detailQ.isPending && detailQ.fetchStatus !== 'idle',
    // Only a GET that never answered is a load error: a refetch that fails
    // keeps the cached detail (the form, the live diligence or the one just
    // created) on screen.
    error: detailQ.isError && !detailQ.data ? detailQ.error : null,
    linkedCase: detail?.linkedCase ?? null,
    liveTask: detail?.liveTask ?? null,
    created,
    createdCount,
    invalidCount,
    rows,
    deadline,
    notes,
    submitted,
    errors,
    busy: mutation.isPending,
    onDeadline: setDeadline,
    onNotes: setNotes,
    onRow: updateRow,
    onSubmit: submit,
  };
}
